// Notes Apocalypse — fake iOS lockscreen entry → coded passcode reveals
// a playing card → after a configurable delay a three-phase cinematic
// reveal hijacks the notes list. EVERY knob (extras pool + count + cadence,
// flicker duration + flash colors, resolve stagger / cycle / frame) lives
// in localStorage so a magician can tune the whole effect from Settings
// without touching code.

import { STORAGE } from "./storage";
import { formatSwiperCard } from "./swiper";

// ---- Passcode -----------------------------------------------------------
export const APOCALYPSE_LENGTHS = [4, 6];

// ---- Trigger delay (between unlock and phase 1 start) ------------------
export const DEFAULT_APOCALYPSE_DELAY = 5;
export const MIN_APOCALYPSE_DELAY = 0;
export const MAX_APOCALYPSE_DELAY = 100;

// ---- Reveal template ----------------------------------------------------
export const DEFAULT_APOCALYPSE_TEMPLATE = "Your card is the (xxx)";

// ---- Phase 0 (wipe — reverse-hack delete of preset note titles) --------
// Per-character delete tick across every preset row simultaneously.
export const DEFAULT_APOCALYPSE_WIPE_FRAME_MS = 70;
export const MIN_APOCALYPSE_WIPE_FRAME_MS = 10;
export const MAX_APOCALYPSE_WIPE_FRAME_MS = 500;
// Beat between the wipe ending (preset notes gone) and Phase-1 spawn.
// Lets the screen sit silent for a moment before the "regen" begins.
export const DEFAULT_APOCALYPSE_POST_WIPE_DELAY_MS = 1500;
export const MIN_APOCALYPSE_POST_WIPE_DELAY_MS = 0;
export const MAX_APOCALYPSE_POST_WIPE_DELAY_MS = 10000;

// ---- Reset gesture (shake-to-reset via DeviceMotion) -------------------
// Net-acceleration delta (m/s²) between consecutive samples that counts
// as a single "peak". Higher = more vigorous shake required.
export const DEFAULT_APOCALYPSE_SHAKE_THRESHOLD = 18;
export const MIN_APOCALYPSE_SHAKE_THRESHOLD = 5;
export const MAX_APOCALYPSE_SHAKE_THRESHOLD = 50;
// Number of peaks within a rolling 1-second window required to trigger
// the reset. Higher = harder to trigger accidentally.
export const DEFAULT_APOCALYPSE_SHAKE_COUNT = 3;
export const MIN_APOCALYPSE_SHAKE_COUNT = 1;
export const MAX_APOCALYPSE_SHAKE_COUNT = 8;

// ---- Native iOS / Safari FX abuses ------------------------------------
// (1) Permission prompt theatre — which system sheet to pop mid-glitch.
export const APOCALYPSE_FX_PERM_TYPES = ["camera", "microphone", "notifications", "geolocation"];
export const DEFAULT_APOCALYPSE_FX_PERM_ENABLED = false;
export const DEFAULT_APOCALYPSE_FX_PERM_TYPE = "camera";
// (2) Speech-synthesis reveal.
export const DEFAULT_APOCALYPSE_FX_SPEECH_ENABLED = false;
export const DEFAULT_APOCALYPSE_FX_SPEECH_TEMPLATE = "Decryption complete. Subject card is the (xxx).";
export const DEFAULT_APOCALYPSE_FX_SPEECH_VOICE = "";
export const DEFAULT_APOCALYPSE_FX_SPEECH_RATE = 0.95;
export const MIN_APOCALYPSE_FX_SPEECH_RATE = 0.5;
export const MAX_APOCALYPSE_FX_SPEECH_RATE = 2.0;
export const DEFAULT_APOCALYPSE_FX_SPEECH_PITCH = 0.7;
export const MIN_APOCALYPSE_FX_SPEECH_PITCH = 0.0;
export const MAX_APOCALYPSE_FX_SPEECH_PITCH = 2.0;
// (3) Clipboard hijack.
export const DEFAULT_APOCALYPSE_FX_CLIPBOARD_ENABLED = false;
export const DEFAULT_APOCALYPSE_FX_CLIPBOARD_TEMPLATE = "Your card is the (xxx).";
// (4) Theme-color status bar flicker.
export const DEFAULT_APOCALYPSE_FX_THEME_ENABLED = false;
export const DEFAULT_APOCALYPSE_FX_THEME_COLORS = ["#000000", "#ff2d3a", "#00e5ff"];
export const DEFAULT_APOCALYPSE_FX_THEME_INTERVAL = 150;
export const MIN_APOCALYPSE_FX_THEME_INTERVAL = 50;
export const MAX_APOCALYPSE_FX_THEME_INTERVAL = 2000;

// ---- Post-empty reset preset -------------------------------------------
// After the spectator shakes (apocalypsePhase: "emptied"), the trick can
// recover in one of two ways:
//   • "instant" — snap straight back to the clean decoy folder
//   • "flash"   — hold the empty beat for `flashDelayMs`, then fire a
//                  short white camera-pop flash N times before resetting
// (The old fake-iOS reboot was retired because iOS doesn't let a PWA
// hide the system status bar, which kept giving the trick away.)
export const APOCALYPSE_RESET_PRESETS = ["instant", "flash"];
export const DEFAULT_APOCALYPSE_RESET_PRESET = "flash";

// Flash preset timing knobs (all in milliseconds, magician-tunable).
export const DEFAULT_APOCALYPSE_FLASH_DELAY_MS = 2000;
export const MIN_APOCALYPSE_FLASH_DELAY_MS = 0;
export const MAX_APOCALYPSE_FLASH_DELAY_MS = 30000;
export const DEFAULT_APOCALYPSE_FLASH_COUNT = 2;
export const MIN_APOCALYPSE_FLASH_COUNT = 1;
export const MAX_APOCALYPSE_FLASH_COUNT = 3;
export const DEFAULT_APOCALYPSE_FLASH_DURATION_MS = 150;
export const MIN_APOCALYPSE_FLASH_DURATION_MS = 50;
export const MAX_APOCALYPSE_FLASH_DURATION_MS = 500;
export const DEFAULT_APOCALYPSE_FLASH_GAP_MS = 120;
export const MIN_APOCALYPSE_FLASH_GAP_MS = 50;
export const MAX_APOCALYPSE_FLASH_GAP_MS = 500;

// ---- Pre-wipe cue dot (the "secret indicator") -------------------------
// A barely-visible dot the magician glances at to know how long until the
// wipe phase fires. Spectator-imperceptible at normal viewing distance
// thanks to the tiny size and low opacity. Every knob below is exposed
// through Settings → Apocalypse so the magician can tune it for their
// performance lighting, dot placement preference, etc.
export const APOCALYPSE_CUE_DOT_POSITIONS = ["TL", "TR", "BL", "BR"];
export const DEFAULT_APOCALYPSE_CUE_DOT_POSITION = "BL";
export const DEFAULT_APOCALYPSE_CUE_DOT_SIZE_PX = 3;

// ---- Default fake-iOS push banners --------------------------------------
// Magician can override these in Settings; defaults are mundane iOS apps
// firing plausible network / sync errors that "explain" why the phone is
// behaving strangely. Each banner appears, lives `pushDurationMs`, slides
// out, and is followed by the next one after `pushIntervalMs`.
export const DEFAULT_APOCALYPSE_PUSH_MESSAGES = [
  { app: "Mail",      title: "Cannot Connect to Server",  body: "iCloud connection lost" },
  { app: "Messages",  title: "Message Send Failed",       body: "Could not deliver to recipient" },
  { app: "WhatsApp",  title: "Connection Error",          body: "Trying to reconnect…" },
  { app: "Calendar",  title: "Sync Failed",               body: "Unable to refresh events" },
  { app: "Photos",    title: "iCloud Sync Paused",        body: "Reduced data mode active" },
  { app: "Settings",  title: "Software Update Failed",    body: "Try again later" },
  { app: "App Store", title: "Verification Required",     body: "Sign in to continue" },
  { app: "Find My",   title: "Location Unavailable",      body: "GPS signal lost" },
];
export const MIN_APOCALYPSE_CUE_DOT_SIZE_PX = 1;
export const MAX_APOCALYPSE_CUE_DOT_SIZE_PX = 16;
export const DEFAULT_APOCALYPSE_CUE_DOT_COLOR = "#000000";
// Opacity stored as an integer percentage 0–100 to keep the input UX
// simple (no decimal points). Rendered as percent/100.
export const DEFAULT_APOCALYPSE_CUE_DOT_OPACITY_PCT = 7;
export const MIN_APOCALYPSE_CUE_DOT_OPACITY_PCT = 0;
export const MAX_APOCALYPSE_CUE_DOT_OPACITY_PCT = 100;
export const DEFAULT_APOCALYPSE_CUE_DOT_LEAD_SEC = 3;
export const MIN_APOCALYPSE_CUE_DOT_LEAD_SEC = 1;
export const MAX_APOCALYPSE_CUE_DOT_LEAD_SEC = 15;

// ---- Wipe mode (Phase 0 style) ------------------------------------------
// "one-by-one" — current behaviour: per-character delete across every
//                preset row simultaneously.
// "flash"      — all preset notes vanish instantly in a single frame.
export const APOCALYPSE_WIPE_MODES = ["one-by-one", "flash"];
export const DEFAULT_APOCALYPSE_WIPE_MODE = "one-by-one";

// ---- Post-reveal "Error" dialog -----------------------------------------
// After the resolve scroll-to-top completes, render an iOS-style alert
// over the still-flickering background. Taps + scroll are blocked; only
// the magician's clock-swipe encoding still registers — they encode a
// SECOND card during this beat. Sound is a synthesised iOS error tritone
// (see apocalypseAudio.playErrorTritone). All knobs configurable.
export const DEFAULT_APOCALYPSE_ERROR_ENABLED = true;
export const DEFAULT_APOCALYPSE_ERROR_TITLE = "Error";
export const DEFAULT_APOCALYPSE_ERROR_BODY = "An unknown error occurred.";
export const DEFAULT_APOCALYPSE_ERROR_SOUND_VOLUME = 80;
// Vibration bed — plays an iPhone-in-silent-mode buzz on loop during
// the haunt phase (wipe + banners + resolve). Stops the moment the
// error dialog appears. Because iOS Safari cannot fire the real
// Vibration API, audio is the only way to sell the buzz.
export const DEFAULT_APOCALYPSE_VIBRATION_ENABLED = true;
export const DEFAULT_APOCALYPSE_VIBRATION_VOLUME = 60;

// ---- Second-prediction template + shake-mode ---------------------------
// Two ways the spectator's shake resolves the error dialog:
//   • "one-shake"  — shake fully resets to clean Notes (legacy)
//   • "two-shake"  — shake #1 dismisses the error AND re-runs the
//                    resolve animation with the just-clock-swiped card
//                    interpolated into the second-prediction template.
//                    Shake #2 fully resets to clean Notes.
export const APOCALYPSE_SHAKE_MODES = ["one-shake", "two-shake"];
export const DEFAULT_APOCALYPSE_SHAKE_MODE = "two-shake";
export const DEFAULT_APOCALYPSE_SECOND_TEMPLATE = "Your card is the (xxx)";

// ---- Reveal scroll speed -----------------------------------------------
export const DEFAULT_APOCALYPSE_SCROLL_SPEED_MS = 600;
export const MIN_APOCALYPSE_SCROLL_SPEED_MS = 0;
export const MAX_APOCALYPSE_SCROLL_SPEED_MS = 2000;

// ---- Post-restore "1-second flash" -------------------------------------
export const DEFAULT_APOCALYPSE_RESTORE_FLASH_ENABLED = true;
export const DEFAULT_APOCALYPSE_RESTORE_FLASH_MS = 1000;
export const MIN_APOCALYPSE_RESTORE_FLASH_MS = 0;
export const MAX_APOCALYPSE_RESTORE_FLASH_MS = 5000;

// ---- Phase 1 (extras spawning) -----------------------------------------
export const DEFAULT_APOCALYPSE_EXTRAS_COUNT = 10;
export const MIN_APOCALYPSE_EXTRAS_COUNT = 0;
export const MAX_APOCALYPSE_EXTRAS_COUNT = 30;
export const DEFAULT_APOCALYPSE_EXTRAS_INTERVAL = 250;
export const MIN_APOCALYPSE_EXTRAS_INTERVAL = 50;
export const MAX_APOCALYPSE_EXTRAS_INTERVAL = 1500;
export const DEFAULT_APOCALYPSE_EXTRAS_TITLES = [
  "Grocery list",
  "Wifi password",
  "Birthday ideas",
  "Books to read",
  "Saved numbers",
  "Recipe — pasta",
  "Mom's address",
  "Gym routine",
  "Weekend plans",
  "Therapist notes",
  "Pharmacy",
  "Tax docs",
  "Packing list",
  "Dentist 3pm",
  "Side project",
];

// ---- Phase 2 (flicker) --------------------------------------------------
export const DEFAULT_APOCALYPSE_FLICKER_MS = 1800;
export const MIN_APOCALYPSE_FLICKER_MS = 0;
export const MAX_APOCALYPSE_FLICKER_MS = 10000;
// Four flash colors used by the screen-flash overlay keyframes. The
// magician can pick any palette (e.g. all greens for Matrix, all reds
// for blood horror, brand colors for a corporate gig).
export const DEFAULT_APOCALYPSE_FLASH_COLORS = ["#ff2d3a", "#00e5ff", "#ffffff", "#b4ff00"];

// ---- Phase 3 (resolve) --------------------------------------------------
export const DEFAULT_APOCALYPSE_ROW_STAGGER = 450;
export const MIN_APOCALYPSE_ROW_STAGGER = 0;
export const MAX_APOCALYPSE_ROW_STAGGER = 2000;
export const DEFAULT_APOCALYPSE_ROW_CYCLE = 1300;
export const MIN_APOCALYPSE_ROW_CYCLE = 100;
export const MAX_APOCALYPSE_ROW_CYCLE = 5000;
export const DEFAULT_APOCALYPSE_ROW_FRAME = 55;
export const MIN_APOCALYPSE_ROW_FRAME = 16;
export const MAX_APOCALYPSE_ROW_FRAME = 300;

const VALUE_MAP = {
  "01": "A", "02": "2", "03": "3", "04": "4", "05": "5",
  "06": "6", "07": "7", "08": "8", "09": "9", "10": "10",
  "11": "J", "12": "Q", "13": "K",
};
const SUIT_MAP = { 1: "S", 2: "H", 3: "C", 4: "D" };

function clampInt(raw, def, min, max) {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return def;
  return Math.max(min, Math.min(max, n));
}

function readColor(key, fallback) {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  // Accept either bare hex or "#hex"; normalise to lowercase 7-char form.
  const v = raw.startsWith("#") ? raw : "#" + raw;
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v.toLowerCase() : fallback;
}

function clampFloat(raw, def, min, max) {
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return def;
  return Math.max(min, Math.min(max, n));
}

function readBool(key, fallback) {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  return raw === "1" || raw === "true";
}

function readPermType(key, fallback) {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  return APOCALYPSE_FX_PERM_TYPES.includes(raw) ? raw : fallback;
}

function readThemeColors(key, fallback) {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback.slice();
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return fallback.slice();
    const cleaned = arr
      .map((s) => (typeof s === "string" ? (s.startsWith("#") ? s : "#" + s) : ""))
      .filter((s) => /^#[0-9a-fA-F]{6}$/.test(s))
      .map((s) => s.toLowerCase());
    return cleaned.length ? cleaned : fallback.slice();
  } catch {
    return fallback.slice();
  }
}

/**
 * Read the full Apocalypse configuration in one shot. All fields have
 * defaults so a never-touched install still works in preview mode.
 */
export function getApocalypseConfig() {
  const lengthRaw = parseInt(localStorage.getItem(STORAGE.APOCALYPSE_LENGTH) || "", 10);
  const length = lengthRaw === 6 ? 6 : 4;

  const extrasTitlesRaw = localStorage.getItem(STORAGE.APOCALYPSE_EXTRAS_TITLES);
  const extrasTitles = extrasTitlesRaw
    ? extrasTitlesRaw
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean)
    : DEFAULT_APOCALYPSE_EXTRAS_TITLES.slice();

  return {
    enabled: localStorage.getItem(STORAGE.APOCALYPSE_ENABLED) === "1",
    length,
    delay: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_DELAY),
      DEFAULT_APOCALYPSE_DELAY,
      MIN_APOCALYPSE_DELAY,
      MAX_APOCALYPSE_DELAY
    ),
    template:
      localStorage.getItem(STORAGE.APOCALYPSE_TEMPLATE) ||
      DEFAULT_APOCALYPSE_TEMPLATE,
    wallpaper: localStorage.getItem(STORAGE.APOCALYPSE_WALLPAPER) || "",
    // Phase 0 (wipe)
    wipeFrameMs: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_WIPE_FRAME_MS),
      DEFAULT_APOCALYPSE_WIPE_FRAME_MS,
      MIN_APOCALYPSE_WIPE_FRAME_MS,
      MAX_APOCALYPSE_WIPE_FRAME_MS
    ),
    postWipeDelayMs: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_POST_WIPE_DELAY_MS),
      DEFAULT_APOCALYPSE_POST_WIPE_DELAY_MS,
      MIN_APOCALYPSE_POST_WIPE_DELAY_MS,
      MAX_APOCALYPSE_POST_WIPE_DELAY_MS
    ),
    shakeThreshold: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_SHAKE_THRESHOLD),
      DEFAULT_APOCALYPSE_SHAKE_THRESHOLD,
      MIN_APOCALYPSE_SHAKE_THRESHOLD,
      MAX_APOCALYPSE_SHAKE_THRESHOLD
    ),
    shakeCount: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_SHAKE_COUNT),
      DEFAULT_APOCALYPSE_SHAKE_COUNT,
      MIN_APOCALYPSE_SHAKE_COUNT,
      MAX_APOCALYPSE_SHAKE_COUNT
    ),
    // Auto-grant: silently call DeviceMotionEvent.requestPermission()
    // on the first lockscreen tap each session. If iOS still remembers
    // a prior grant the call resolves invisibly; otherwise the prompt
    // appears at the natural moment the spectator unlocks. Default ON.
    autoMotion:
      localStorage.getItem(STORAGE.APOCALYPSE_AUTO_MOTION) === null
        ? true
        : localStorage.getItem(STORAGE.APOCALYPSE_AUTO_MOTION) === "1",
    // Notification ping fired on each spawning extra during the glitch.
    soundEnabled:
      localStorage.getItem(STORAGE.APOCALYPSE_SOUND_ENABLED) === null
        ? true
        : localStorage.getItem(STORAGE.APOCALYPSE_SOUND_ENABLED) === "1",
    soundVolume: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_SOUND_VOLUME),
      70, 0, 100
    ),
    // Fake iOS push-message banners overlaid during the glitch.
    pushEnabled:
      localStorage.getItem(STORAGE.APOCALYPSE_PUSH_ENABLED) === null
        ? true
        : localStorage.getItem(STORAGE.APOCALYPSE_PUSH_ENABLED) === "1",
    // Per-banner notification ping (tri-tone). Separate from row-spawn
    // sound so the magician can silence the banner sound alone — the
    // row spawns themselves are now silent by design (Feb 2026).
    pushSoundEnabled:
      localStorage.getItem(STORAGE.APOCALYPSE_PUSH_SOUND_ENABLED) === null
        ? true
        : localStorage.getItem(STORAGE.APOCALYPSE_PUSH_SOUND_ENABLED) === "1",
    pushIntervalMs: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_PUSH_INTERVAL_MS),
      350, 80, 5000
    ),
    pushDurationMs: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_PUSH_DURATION_MS),
      2400, 500, 8000
    ),
    pushMessages: (() => {
      try {
        const raw = localStorage.getItem(STORAGE.APOCALYPSE_PUSH_MESSAGES);
        if (!raw) return DEFAULT_APOCALYPSE_PUSH_MESSAGES;
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr) || arr.length === 0) return DEFAULT_APOCALYPSE_PUSH_MESSAGES;
        return arr;
      } catch {
        return DEFAULT_APOCALYPSE_PUSH_MESSAGES;
      }
    })(),
    // Phase 1
    extrasCount: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_EXTRAS_COUNT),
      DEFAULT_APOCALYPSE_EXTRAS_COUNT,
      MIN_APOCALYPSE_EXTRAS_COUNT,
      MAX_APOCALYPSE_EXTRAS_COUNT
    ),
    extrasInterval: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_EXTRAS_INTERVAL),
      DEFAULT_APOCALYPSE_EXTRAS_INTERVAL,
      MIN_APOCALYPSE_EXTRAS_INTERVAL,
      MAX_APOCALYPSE_EXTRAS_INTERVAL
    ),
    extrasTitles,
    // Phase 2
    flickerMs: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_FLICKER_MS),
      DEFAULT_APOCALYPSE_FLICKER_MS,
      MIN_APOCALYPSE_FLICKER_MS,
      MAX_APOCALYPSE_FLICKER_MS
    ),
    flashColors: [
      readColor(STORAGE.APOCALYPSE_FLASH_1, DEFAULT_APOCALYPSE_FLASH_COLORS[0]),
      readColor(STORAGE.APOCALYPSE_FLASH_2, DEFAULT_APOCALYPSE_FLASH_COLORS[1]),
      readColor(STORAGE.APOCALYPSE_FLASH_3, DEFAULT_APOCALYPSE_FLASH_COLORS[2]),
      readColor(STORAGE.APOCALYPSE_FLASH_4, DEFAULT_APOCALYPSE_FLASH_COLORS[3]),
    ],
    // Phase 3
    rowStagger: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_ROW_STAGGER),
      DEFAULT_APOCALYPSE_ROW_STAGGER,
      MIN_APOCALYPSE_ROW_STAGGER,
      MAX_APOCALYPSE_ROW_STAGGER
    ),
    rowCycle: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_ROW_CYCLE),
      DEFAULT_APOCALYPSE_ROW_CYCLE,
      MIN_APOCALYPSE_ROW_CYCLE,
      MAX_APOCALYPSE_ROW_CYCLE
    ),
    rowFrame: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_ROW_FRAME),
      DEFAULT_APOCALYPSE_ROW_FRAME,
      MIN_APOCALYPSE_ROW_FRAME,
      MAX_APOCALYPSE_ROW_FRAME
    ),
    // Native FX abuses
    fxPermEnabled: readBool(STORAGE.APOCALYPSE_FX_PERM_ENABLED, DEFAULT_APOCALYPSE_FX_PERM_ENABLED),
    fxPermType: readPermType(STORAGE.APOCALYPSE_FX_PERM_TYPE, DEFAULT_APOCALYPSE_FX_PERM_TYPE),
    fxSpeechEnabled: readBool(STORAGE.APOCALYPSE_FX_SPEECH_ENABLED, DEFAULT_APOCALYPSE_FX_SPEECH_ENABLED),
    fxSpeechTemplate: localStorage.getItem(STORAGE.APOCALYPSE_FX_SPEECH_TEMPLATE) || DEFAULT_APOCALYPSE_FX_SPEECH_TEMPLATE,
    fxSpeechVoice: localStorage.getItem(STORAGE.APOCALYPSE_FX_SPEECH_VOICE) || DEFAULT_APOCALYPSE_FX_SPEECH_VOICE,
    fxSpeechRate: clampFloat(
      localStorage.getItem(STORAGE.APOCALYPSE_FX_SPEECH_RATE),
      DEFAULT_APOCALYPSE_FX_SPEECH_RATE,
      MIN_APOCALYPSE_FX_SPEECH_RATE,
      MAX_APOCALYPSE_FX_SPEECH_RATE
    ),
    fxSpeechPitch: clampFloat(
      localStorage.getItem(STORAGE.APOCALYPSE_FX_SPEECH_PITCH),
      DEFAULT_APOCALYPSE_FX_SPEECH_PITCH,
      MIN_APOCALYPSE_FX_SPEECH_PITCH,
      MAX_APOCALYPSE_FX_SPEECH_PITCH
    ),
    fxClipboardEnabled: readBool(STORAGE.APOCALYPSE_FX_CLIPBOARD_ENABLED, DEFAULT_APOCALYPSE_FX_CLIPBOARD_ENABLED),
    fxClipboardTemplate: localStorage.getItem(STORAGE.APOCALYPSE_FX_CLIPBOARD_TEMPLATE) || DEFAULT_APOCALYPSE_FX_CLIPBOARD_TEMPLATE,
    fxThemeEnabled: readBool(STORAGE.APOCALYPSE_FX_THEME_ENABLED, DEFAULT_APOCALYPSE_FX_THEME_ENABLED),
    fxThemeColors: readThemeColors(STORAGE.APOCALYPSE_FX_THEME_COLORS, DEFAULT_APOCALYPSE_FX_THEME_COLORS),
    fxThemeInterval: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_FX_THEME_INTERVAL),
      DEFAULT_APOCALYPSE_FX_THEME_INTERVAL,
      MIN_APOCALYPSE_FX_THEME_INTERVAL,
      MAX_APOCALYPSE_FX_THEME_INTERVAL
    ),
    resetPreset: APOCALYPSE_RESET_PRESETS.includes(
      localStorage.getItem(STORAGE.APOCALYPSE_RESET_PRESET)
    )
      ? localStorage.getItem(STORAGE.APOCALYPSE_RESET_PRESET)
      : DEFAULT_APOCALYPSE_RESET_PRESET,
    flashDelayMs: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_FLASH_DELAY_MS),
      DEFAULT_APOCALYPSE_FLASH_DELAY_MS,
      MIN_APOCALYPSE_FLASH_DELAY_MS,
      MAX_APOCALYPSE_FLASH_DELAY_MS
    ),
    flashCount: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_FLASH_COUNT),
      DEFAULT_APOCALYPSE_FLASH_COUNT,
      MIN_APOCALYPSE_FLASH_COUNT,
      MAX_APOCALYPSE_FLASH_COUNT
    ),
    flashDurationMs: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_FLASH_DURATION_MS),
      DEFAULT_APOCALYPSE_FLASH_DURATION_MS,
      MIN_APOCALYPSE_FLASH_DURATION_MS,
      MAX_APOCALYPSE_FLASH_DURATION_MS
    ),
    flashGapMs: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_FLASH_GAP_MS),
      DEFAULT_APOCALYPSE_FLASH_GAP_MS,
      MIN_APOCALYPSE_FLASH_GAP_MS,
      MAX_APOCALYPSE_FLASH_GAP_MS
    ),
    cueDot: {
      position: APOCALYPSE_CUE_DOT_POSITIONS.includes(
        localStorage.getItem(STORAGE.APOCALYPSE_CUE_DOT_POSITION)
      )
        ? localStorage.getItem(STORAGE.APOCALYPSE_CUE_DOT_POSITION)
        : DEFAULT_APOCALYPSE_CUE_DOT_POSITION,
      sizePx: clampInt(
        localStorage.getItem(STORAGE.APOCALYPSE_CUE_DOT_SIZE_PX),
        DEFAULT_APOCALYPSE_CUE_DOT_SIZE_PX,
        MIN_APOCALYPSE_CUE_DOT_SIZE_PX,
        MAX_APOCALYPSE_CUE_DOT_SIZE_PX
      ),
      color:
        localStorage.getItem(STORAGE.APOCALYPSE_CUE_DOT_COLOR) ||
        DEFAULT_APOCALYPSE_CUE_DOT_COLOR,
      opacityPct: clampInt(
        localStorage.getItem(STORAGE.APOCALYPSE_CUE_DOT_OPACITY_PCT),
        DEFAULT_APOCALYPSE_CUE_DOT_OPACITY_PCT,
        MIN_APOCALYPSE_CUE_DOT_OPACITY_PCT,
        MAX_APOCALYPSE_CUE_DOT_OPACITY_PCT
      ),
      leadSec: clampInt(
        localStorage.getItem(STORAGE.APOCALYPSE_CUE_DOT_LEAD_SEC),
        DEFAULT_APOCALYPSE_CUE_DOT_LEAD_SEC,
        MIN_APOCALYPSE_CUE_DOT_LEAD_SEC,
        MAX_APOCALYPSE_CUE_DOT_LEAD_SEC
      ),
    },
    // Wipe mode — selects between the legacy per-character delete
    // (Phase-0 "one-by-one") and an instant-vanish "flash" alternative.
    wipeMode: APOCALYPSE_WIPE_MODES.includes(
      localStorage.getItem(STORAGE.APOCALYPSE_WIPE_MODE)
    )
      ? localStorage.getItem(STORAGE.APOCALYPSE_WIPE_MODE)
      : DEFAULT_APOCALYPSE_WIPE_MODE,
    // Post-reveal error dialog
    errorEnabled:
      localStorage.getItem(STORAGE.APOCALYPSE_ERROR_ENABLED) === null
        ? DEFAULT_APOCALYPSE_ERROR_ENABLED
        : localStorage.getItem(STORAGE.APOCALYPSE_ERROR_ENABLED) === "1",
    errorTitle:
      localStorage.getItem(STORAGE.APOCALYPSE_ERROR_TITLE) ||
      DEFAULT_APOCALYPSE_ERROR_TITLE,
    errorBody:
      localStorage.getItem(STORAGE.APOCALYPSE_ERROR_BODY) ||
      DEFAULT_APOCALYPSE_ERROR_BODY,
    errorSoundVolume: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_ERROR_SOUND_VOLUME),
      DEFAULT_APOCALYPSE_ERROR_SOUND_VOLUME,
      0,
      100
    ),
    // Vibration bed (haunt-phase looping MP3)
    vibrationEnabled:
      localStorage.getItem(STORAGE.APOCALYPSE_VIBRATION_ENABLED) === null
        ? DEFAULT_APOCALYPSE_VIBRATION_ENABLED
        : localStorage.getItem(STORAGE.APOCALYPSE_VIBRATION_ENABLED) === "1",
    vibrationVolume: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_VIBRATION_VOLUME),
      DEFAULT_APOCALYPSE_VIBRATION_VOLUME,
      0,
      100
    ),
    // Second prediction sentence (the "swiped during error" reveal).
    secondTemplate:
      localStorage.getItem(STORAGE.APOCALYPSE_SECOND_TEMPLATE) ||
      DEFAULT_APOCALYPSE_SECOND_TEMPLATE,
    // Shake resolution mode.
    shakeMode: APOCALYPSE_SHAKE_MODES.includes(
      localStorage.getItem(STORAGE.APOCALYPSE_SHAKE_MODE)
    )
      ? localStorage.getItem(STORAGE.APOCALYPSE_SHAKE_MODE)
      : DEFAULT_APOCALYPSE_SHAKE_MODE,
    // Reveal scroll speed (ms) per row.
    scrollSpeedMs: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_SCROLL_SPEED_MS),
      DEFAULT_APOCALYPSE_SCROLL_SPEED_MS,
      MIN_APOCALYPSE_SCROLL_SPEED_MS,
      MAX_APOCALYPSE_SCROLL_SPEED_MS
    ),
    // Post-restore 1-second flash.
    restoreFlashEnabled:
      localStorage.getItem(STORAGE.APOCALYPSE_RESTORE_FLASH_ENABLED) === null
        ? DEFAULT_APOCALYPSE_RESTORE_FLASH_ENABLED
        : localStorage.getItem(STORAGE.APOCALYPSE_RESTORE_FLASH_ENABLED) === "1",
    restoreFlashMs: clampInt(
      localStorage.getItem(STORAGE.APOCALYPSE_RESTORE_FLASH_MS),
      DEFAULT_APOCALYPSE_RESTORE_FLASH_MS,
      MIN_APOCALYPSE_RESTORE_FLASH_MS,
      MAX_APOCALYPSE_RESTORE_FLASH_MS
    ),
  };
}

/**
 * Decode the magician's coded passcode into a `{ value, suit }` card.
 * Returns null on a non-card code (wrong-passcode shake).
 */
export function decodeApocalypsePasscode(digits) {
  if (!digits || typeof digits !== "string" || digits.length < 3) return null;
  if (!/^\d+$/.test(digits)) return null;
  const valKey = digits.slice(0, 2);
  const suitDigit = parseInt(digits.charAt(2), 10);
  const value = VALUE_MAP[valKey];
  const suit = SUIT_MAP[suitDigit];
  if (!value || !suit) return null;
  return { value, suit };
}

export function setApocalypseArmed(card, delaySec) {
  if (!card || !card.value || !card.suit) return;
  try {
    localStorage.setItem(
      STORAGE.APOCALYPSE_ARMED,
      JSON.stringify({
        value: card.value,
        suit: card.suit,
        armedAt: Date.now(),
        delay: Number.isFinite(delaySec) ? delaySec : DEFAULT_APOCALYPSE_DELAY,
      })
    );
  } catch {
    /* localStorage may be full or disabled — fine to drop silently. */
  }
}

export function readApocalypseArmed() {
  try {
    const raw = localStorage.getItem(STORAGE.APOCALYPSE_ARMED);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.value || !parsed.suit) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearApocalypseArmed() {
  localStorage.removeItem(STORAGE.APOCALYPSE_ARMED);
}

// ---- Rehearsal flag ----------------------------------------------------
// Set by the Settings → Apocalypse "Rehearse trick" button right before
// navigating to /lock. When the trick finishes its full reboot teardown
// in DecoyNotes, the cleanup checks-and-clears this flag and bounces
// the magician back to /settings instead of leaving them on the clean
// Notes folder — saves a manual triple-tap escape between rehearsals.
export function setApocalypseRehearsing(on) {
  try {
    if (on) localStorage.setItem(STORAGE.APOCALYPSE_REHEARSING, "1");
    else localStorage.removeItem(STORAGE.APOCALYPSE_REHEARSING);
  } catch {
    /* ignore */
  }
}

export function consumeApocalypseRehearsing() {
  try {
    const on = localStorage.getItem(STORAGE.APOCALYPSE_REHEARSING) === "1";
    if (on) localStorage.removeItem(STORAGE.APOCALYPSE_REHEARSING);
    return on;
  } catch {
    return false;
  }
}

export function formatApocalypseCard(card, template, langCode) {
  return formatSwiperCard(
    card,
    "custom",
    template || DEFAULT_APOCALYPSE_TEMPLATE,
    langCode
  );
}

/**
 * Return just the bare card label ("four of spades", "ace of hearts"…)
 * with no surrounding template text. Used by the speech & clipboard FX
 * which want to interpolate the card into their own templates.
 */
export function apocalypseCardLabel(card, langCode) {
  if (!card || !card.value || !card.suit) return "";
  // formatSwiperCard with a pure-placeholder template returns just the
  // localised card name.
  return formatSwiperCard(card, "custom", "(xxx)", langCode);
}

// ---- Persistence helpers (clamped, normalised) -------------------------
export function saveApocalypseLength(n) {
  const v = parseInt(n, 10) === 6 ? 6 : 4;
  localStorage.setItem(STORAGE.APOCALYPSE_LENGTH, String(v));
}
export function saveApocalypseDelay(s) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_DELAY,
    String(clampInt(s, DEFAULT_APOCALYPSE_DELAY, MIN_APOCALYPSE_DELAY, MAX_APOCALYPSE_DELAY))
  );
}
export function saveApocalypseWipeFrameMs(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_WIPE_FRAME_MS,
    String(clampInt(n, DEFAULT_APOCALYPSE_WIPE_FRAME_MS, MIN_APOCALYPSE_WIPE_FRAME_MS, MAX_APOCALYPSE_WIPE_FRAME_MS))
  );
}
export function saveApocalypsePostWipeDelayMs(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_POST_WIPE_DELAY_MS,
    String(clampInt(n, DEFAULT_APOCALYPSE_POST_WIPE_DELAY_MS, MIN_APOCALYPSE_POST_WIPE_DELAY_MS, MAX_APOCALYPSE_POST_WIPE_DELAY_MS))
  );
}
export function saveApocalypseShakeThreshold(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_SHAKE_THRESHOLD,
    String(clampInt(n, DEFAULT_APOCALYPSE_SHAKE_THRESHOLD, MIN_APOCALYPSE_SHAKE_THRESHOLD, MAX_APOCALYPSE_SHAKE_THRESHOLD))
  );
}
export function saveApocalypseShakeCount(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_SHAKE_COUNT,
    String(clampInt(n, DEFAULT_APOCALYPSE_SHAKE_COUNT, MIN_APOCALYPSE_SHAKE_COUNT, MAX_APOCALYPSE_SHAKE_COUNT))
  );
}
export function saveApocalypseAutoMotion(on) {
  localStorage.setItem(STORAGE.APOCALYPSE_AUTO_MOTION, on ? "1" : "0");
}

export function saveApocalypseSoundEnabled(on) {
  localStorage.setItem(STORAGE.APOCALYPSE_SOUND_ENABLED, on ? "1" : "0");
}
export function saveApocalypseSoundVolume(n) {
  localStorage.setItem(STORAGE.APOCALYPSE_SOUND_VOLUME, String(clampInt(n, 70, 0, 100)));
}

export function saveApocalypsePushEnabled(on) {
  localStorage.setItem(STORAGE.APOCALYPSE_PUSH_ENABLED, on ? "1" : "0");
}
export function saveApocalypsePushSoundEnabled(on) {
  localStorage.setItem(STORAGE.APOCALYPSE_PUSH_SOUND_ENABLED, on ? "1" : "0");
}
export function saveApocalypsePushIntervalMs(n) {
  localStorage.setItem(STORAGE.APOCALYPSE_PUSH_INTERVAL_MS, String(clampInt(n, 350, 80, 5000)));
}
export function saveApocalypsePushDurationMs(n) {
  localStorage.setItem(STORAGE.APOCALYPSE_PUSH_DURATION_MS, String(clampInt(n, 2400, 500, 8000)));
}
export function saveApocalypsePushMessages(arr) {
  if (!Array.isArray(arr)) return;
  try {
    localStorage.setItem(STORAGE.APOCALYPSE_PUSH_MESSAGES, JSON.stringify(arr));
  } catch { /* ignore quota */ }
}
export function saveApocalypseExtrasCount(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_EXTRAS_COUNT,
    String(clampInt(n, DEFAULT_APOCALYPSE_EXTRAS_COUNT, MIN_APOCALYPSE_EXTRAS_COUNT, MAX_APOCALYPSE_EXTRAS_COUNT))
  );
}
export function saveApocalypseExtrasInterval(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_EXTRAS_INTERVAL,
    String(clampInt(n, DEFAULT_APOCALYPSE_EXTRAS_INTERVAL, MIN_APOCALYPSE_EXTRAS_INTERVAL, MAX_APOCALYPSE_EXTRAS_INTERVAL))
  );
}
export function saveApocalypseExtrasTitles(titles) {
  // Accept either an array or a newline-separated string. Trim + dedupe-
  // free (we DO allow dupes since the magician may intentionally repeat).
  const arr = Array.isArray(titles)
    ? titles
    : String(titles || "").split("\n");
  const cleaned = arr.map((s) => s.trim()).filter(Boolean);
  localStorage.setItem(STORAGE.APOCALYPSE_EXTRAS_TITLES, cleaned.join("\n"));
}
export function saveApocalypseFlickerMs(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_FLICKER_MS,
    String(clampInt(n, DEFAULT_APOCALYPSE_FLICKER_MS, MIN_APOCALYPSE_FLICKER_MS, MAX_APOCALYPSE_FLICKER_MS))
  );
}
export function saveApocalypseFlashColor(slot, hex) {
  const v = (hex || "").startsWith("#") ? hex : "#" + (hex || "");
  if (!/^#[0-9a-fA-F]{6}$/.test(v)) return;
  const key = [
    STORAGE.APOCALYPSE_FLASH_1,
    STORAGE.APOCALYPSE_FLASH_2,
    STORAGE.APOCALYPSE_FLASH_3,
    STORAGE.APOCALYPSE_FLASH_4,
  ][slot];
  if (key) localStorage.setItem(key, v.toLowerCase());
}
export function saveApocalypseRowStagger(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_ROW_STAGGER,
    String(clampInt(n, DEFAULT_APOCALYPSE_ROW_STAGGER, MIN_APOCALYPSE_ROW_STAGGER, MAX_APOCALYPSE_ROW_STAGGER))
  );
}
export function saveApocalypseRowCycle(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_ROW_CYCLE,
    String(clampInt(n, DEFAULT_APOCALYPSE_ROW_CYCLE, MIN_APOCALYPSE_ROW_CYCLE, MAX_APOCALYPSE_ROW_CYCLE))
  );
}
export function saveApocalypseRowFrame(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_ROW_FRAME,
    String(clampInt(n, DEFAULT_APOCALYPSE_ROW_FRAME, MIN_APOCALYPSE_ROW_FRAME, MAX_APOCALYPSE_ROW_FRAME))
  );
}


// ---- FX save helpers ---------------------------------------------------
function writeBool(key, val) {
  localStorage.setItem(key, val ? "1" : "0");
}
export function saveApocalypseFxPermEnabled(b) {
  writeBool(STORAGE.APOCALYPSE_FX_PERM_ENABLED, !!b);
}
export function saveApocalypseFxPermType(s) {
  const v = APOCALYPSE_FX_PERM_TYPES.includes(s) ? s : DEFAULT_APOCALYPSE_FX_PERM_TYPE;
  localStorage.setItem(STORAGE.APOCALYPSE_FX_PERM_TYPE, v);
}
export function saveApocalypseFxSpeechEnabled(b) {
  writeBool(STORAGE.APOCALYPSE_FX_SPEECH_ENABLED, !!b);
}
export function saveApocalypseFxSpeechTemplate(s) {
  const v = typeof s === "string" ? s : DEFAULT_APOCALYPSE_FX_SPEECH_TEMPLATE;
  localStorage.setItem(STORAGE.APOCALYPSE_FX_SPEECH_TEMPLATE, v);
}
export function saveApocalypseFxSpeechVoice(uri) {
  localStorage.setItem(STORAGE.APOCALYPSE_FX_SPEECH_VOICE, typeof uri === "string" ? uri : "");
}
export function saveApocalypseFxSpeechRate(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_FX_SPEECH_RATE,
    String(clampFloat(n, DEFAULT_APOCALYPSE_FX_SPEECH_RATE, MIN_APOCALYPSE_FX_SPEECH_RATE, MAX_APOCALYPSE_FX_SPEECH_RATE))
  );
}
export function saveApocalypseFxSpeechPitch(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_FX_SPEECH_PITCH,
    String(clampFloat(n, DEFAULT_APOCALYPSE_FX_SPEECH_PITCH, MIN_APOCALYPSE_FX_SPEECH_PITCH, MAX_APOCALYPSE_FX_SPEECH_PITCH))
  );
}
export function saveApocalypseFxClipboardEnabled(b) {
  writeBool(STORAGE.APOCALYPSE_FX_CLIPBOARD_ENABLED, !!b);
}
export function saveApocalypseFxClipboardTemplate(s) {
  const v = typeof s === "string" ? s : DEFAULT_APOCALYPSE_FX_CLIPBOARD_TEMPLATE;
  localStorage.setItem(STORAGE.APOCALYPSE_FX_CLIPBOARD_TEMPLATE, v);
}
export function saveApocalypseFxThemeEnabled(b) {
  writeBool(STORAGE.APOCALYPSE_FX_THEME_ENABLED, !!b);
}
export function saveApocalypseFxThemeColors(arr) {
  const cleaned = (Array.isArray(arr) ? arr : [])
    .map((s) => (typeof s === "string" ? (s.startsWith("#") ? s : "#" + s) : ""))
    .filter((s) => /^#[0-9a-fA-F]{6}$/.test(s))
    .map((s) => s.toLowerCase());
  const final = cleaned.length ? cleaned : DEFAULT_APOCALYPSE_FX_THEME_COLORS;
  localStorage.setItem(STORAGE.APOCALYPSE_FX_THEME_COLORS, JSON.stringify(final));
}
export function saveApocalypseFxThemeInterval(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_FX_THEME_INTERVAL,
    String(clampInt(n, DEFAULT_APOCALYPSE_FX_THEME_INTERVAL, MIN_APOCALYPSE_FX_THEME_INTERVAL, MAX_APOCALYPSE_FX_THEME_INTERVAL))
  );
}
export function saveApocalypseResetPreset(p) {
  const v = APOCALYPSE_RESET_PRESETS.includes(p) ? p : DEFAULT_APOCALYPSE_RESET_PRESET;
  localStorage.setItem(STORAGE.APOCALYPSE_RESET_PRESET, v);
}
export function saveApocalypseFlashDelayMs(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_FLASH_DELAY_MS,
    String(clampInt(n, DEFAULT_APOCALYPSE_FLASH_DELAY_MS, MIN_APOCALYPSE_FLASH_DELAY_MS, MAX_APOCALYPSE_FLASH_DELAY_MS))
  );
}
export function saveApocalypseFlashCount(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_FLASH_COUNT,
    String(clampInt(n, DEFAULT_APOCALYPSE_FLASH_COUNT, MIN_APOCALYPSE_FLASH_COUNT, MAX_APOCALYPSE_FLASH_COUNT))
  );
}
export function saveApocalypseFlashDurationMs(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_FLASH_DURATION_MS,
    String(clampInt(n, DEFAULT_APOCALYPSE_FLASH_DURATION_MS, MIN_APOCALYPSE_FLASH_DURATION_MS, MAX_APOCALYPSE_FLASH_DURATION_MS))
  );
}
export function saveApocalypseFlashGapMs(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_FLASH_GAP_MS,
    String(clampInt(n, DEFAULT_APOCALYPSE_FLASH_GAP_MS, MIN_APOCALYPSE_FLASH_GAP_MS, MAX_APOCALYPSE_FLASH_GAP_MS))
  );
}

// ---- Cue-dot save helpers ----------------------------------------------
export function saveApocalypseCueDotPosition(p) {
  const v = APOCALYPSE_CUE_DOT_POSITIONS.includes(p)
    ? p
    : DEFAULT_APOCALYPSE_CUE_DOT_POSITION;
  localStorage.setItem(STORAGE.APOCALYPSE_CUE_DOT_POSITION, v);
}
export function saveApocalypseCueDotSizePx(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_CUE_DOT_SIZE_PX,
    String(
      clampInt(
        n,
        DEFAULT_APOCALYPSE_CUE_DOT_SIZE_PX,
        MIN_APOCALYPSE_CUE_DOT_SIZE_PX,
        MAX_APOCALYPSE_CUE_DOT_SIZE_PX
      )
    )
  );
}
export function saveApocalypseCueDotColor(s) {
  const v = typeof s === "string" ? s : "";
  const hex = v.startsWith("#") ? v : "#" + v;
  const final = /^#[0-9a-fA-F]{6}$/.test(hex)
    ? hex.toLowerCase()
    : DEFAULT_APOCALYPSE_CUE_DOT_COLOR;
  localStorage.setItem(STORAGE.APOCALYPSE_CUE_DOT_COLOR, final);
}
export function saveApocalypseCueDotOpacityPct(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_CUE_DOT_OPACITY_PCT,
    String(
      clampInt(
        n,
        DEFAULT_APOCALYPSE_CUE_DOT_OPACITY_PCT,
        MIN_APOCALYPSE_CUE_DOT_OPACITY_PCT,
        MAX_APOCALYPSE_CUE_DOT_OPACITY_PCT
      )
    )
  );
}
export function saveApocalypseCueDotLeadSec(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_CUE_DOT_LEAD_SEC,
    String(
      clampInt(
        n,
        DEFAULT_APOCALYPSE_CUE_DOT_LEAD_SEC,
        MIN_APOCALYPSE_CUE_DOT_LEAD_SEC,
        MAX_APOCALYPSE_CUE_DOT_LEAD_SEC
      )
    )
  );
}

// ---- Pass-4 save helpers -----------------------------------------------
export function saveApocalypseWipeMode(s) {
  const v = APOCALYPSE_WIPE_MODES.includes(s) ? s : DEFAULT_APOCALYPSE_WIPE_MODE;
  localStorage.setItem(STORAGE.APOCALYPSE_WIPE_MODE, v);
}
export function saveApocalypseErrorEnabled(b) {
  localStorage.setItem(STORAGE.APOCALYPSE_ERROR_ENABLED, b ? "1" : "0");
}
export function saveApocalypseErrorTitle(s) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_ERROR_TITLE,
    (s || "").slice(0, 80) || DEFAULT_APOCALYPSE_ERROR_TITLE
  );
}
export function saveApocalypseErrorBody(s) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_ERROR_BODY,
    (s || "").slice(0, 240) || DEFAULT_APOCALYPSE_ERROR_BODY
  );
}
export function saveApocalypseErrorSoundVolume(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_ERROR_SOUND_VOLUME,
    String(clampInt(n, DEFAULT_APOCALYPSE_ERROR_SOUND_VOLUME, 0, 100))
  );
}
export function saveApocalypseVibrationEnabled(b) {
  localStorage.setItem(STORAGE.APOCALYPSE_VIBRATION_ENABLED, b ? "1" : "0");
}
export function saveApocalypseVibrationVolume(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_VIBRATION_VOLUME,
    String(clampInt(n, DEFAULT_APOCALYPSE_VIBRATION_VOLUME, 0, 100))
  );
}
export function saveApocalypseSecondTemplate(s) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_SECOND_TEMPLATE,
    (s || "").slice(0, 240) || DEFAULT_APOCALYPSE_SECOND_TEMPLATE
  );
}
export function saveApocalypseShakeMode(s) {
  const v = APOCALYPSE_SHAKE_MODES.includes(s) ? s : DEFAULT_APOCALYPSE_SHAKE_MODE;
  localStorage.setItem(STORAGE.APOCALYPSE_SHAKE_MODE, v);
}
export function saveApocalypseScrollSpeedMs(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_SCROLL_SPEED_MS,
    String(clampInt(n, DEFAULT_APOCALYPSE_SCROLL_SPEED_MS, MIN_APOCALYPSE_SCROLL_SPEED_MS, MAX_APOCALYPSE_SCROLL_SPEED_MS))
  );
}
export function saveApocalypseRestoreFlashEnabled(b) {
  localStorage.setItem(STORAGE.APOCALYPSE_RESTORE_FLASH_ENABLED, b ? "1" : "0");
}
export function saveApocalypseRestoreFlashMs(n) {
  localStorage.setItem(
    STORAGE.APOCALYPSE_RESTORE_FLASH_MS,
    String(clampInt(n, DEFAULT_APOCALYPSE_RESTORE_FLASH_MS, MIN_APOCALYPSE_RESTORE_FLASH_MS, MAX_APOCALYPSE_RESTORE_FLASH_MS))
  );
}

